import type { BatchCell } from "@/types";
import { evaluationOutcomeFrom } from "@/utils/evaluation";
import {
  formatScore,
  incomparableReason,
  incomparableTag,
  isComparable,
  isCommon,
  scoreBucket,
} from "@/utils/rubric";

/**
 * One cell of a run, as the page reads it.
 *
 * Two sources and never mixed: while a TC is in flight its **item** says what
 * is happening (대기 · 실행 중 · 실패 · 제외); once its replay finished, the
 * **verdict** of that replay says what it scored (C5). A finished replay the
 * scorer has not reached yet is 채점 대기 — not 0 and not 평가 불가.
 */
export type CellTone =
  | "none"
  | "queued"
  | "running"
  | "failed"
  | "skipped"
  | "unscored"
  | "na"
  /** A score on another rubric or policy — shown, never bucketed or averaged. */
  | "legacy"
  | "hi"
  | "mid"
  | "lo";

export type CellView = { text: string; tone: CellTone; title?: string };

/**
 * How each tone looks. Shared by Batch Test's rows and the Matrix so a cell
 * reads the same on both — sequential for scores (not success colours),
 * dashed for "no score yet", destructive only for a failure.
 */
export const TONE_CLASS: Record<CellTone, string> = {
  none: "text-muted-foreground",
  queued: "text-muted-foreground",
  running: "text-primary",
  failed: "border border-destructive-line bg-destructive-surface text-destructive",
  skipped: "text-muted-foreground line-through",
  unscored: "border border-dashed border-border text-muted-foreground",
  na: "border border-dashed border-border text-muted-foreground",
  legacy: "border border-border text-muted-foreground",
  hi: "bg-primary text-primary-foreground",
  mid: "bg-accent text-accent-foreground",
  lo: "bg-muted text-foreground",
};

/** Sequential buckets over a rubric v2 score, 0–1 (`utils/rubric`). */
export function scoreTone(score: number): CellTone {
  return scoreBucket(score);
}

function partsTitle(answerQuality: number | null | undefined, execution: number | null | undefined) {
  if (answerQuality == null && execution == null) return undefined;
  const part = (v: number | null | undefined) => (v == null ? "N/A" : formatScore(v));
  return `Final = Answer Quality ${part(answerQuality)} · Execution ${part(execution)}`;
}

export function cellView(cell: BatchCell | undefined): CellView {
  if (!cell) return { text: "실행 전", tone: "none" };
  switch (cell.status) {
    case "pending":
      return { text: "대기", tone: "queued" };
    case "running":
      return { text: "실행 중", tone: "running" };
    case "skipped":
      return { text: "제외", tone: "skipped", title: cell.error ?? undefined };
    case "failed":
      return { text: "실패", tone: "failed", title: cell.error ?? undefined };
    case "done": {
      const outcome = evaluationOutcomeFrom(cell.evaluation);
      switch (outcome.status) {
        case "scored":
          // A v1 score keeps its number but not a colour: on the v2 buckets
          // every 1–5 mean would read as the top one (be/RUBRIC.md R10).
          if (isCommon(outcome)) {
            return {
              text: `${formatScore(outcome.score)} · common`,
              tone: scoreTone(outcome.score),
              title: [incomparableReason(outcome), partsTitle(outcome.answer_quality, outcome.execution)]
                .filter(Boolean)
                .join("\n"),
            };
          }
          return isComparable(outcome)
            ? {
                text: formatScore(outcome.score),
                tone: scoreTone(outcome.score),
                // v2's other two scores — "answered worse" or "ran slower".
                title: partsTitle(outcome.answer_quality, outcome.execution),
              }
            : {
                text: `${formatScore(outcome.score)} · ${incomparableTag(outcome)}`,
                tone: "legacy",
                title: incomparableReason(outcome),
              };
        case "not_evaluable":
          return outcome.reason === "not_scored_yet"
            ? { text: "채점 대기", tone: "unscored" }
            : { text: "평가 불가", tone: "na", title: "검토에서 통과한 Reference Answer 가 없습니다" };
        case "failed":
          return { text: "실패", tone: "failed", title: outcome.reason ?? undefined };
        case "not_run":
          return { text: "채점 대기", tone: "unscored" };
      }
    }
  }
}

export type RunStats = {
  total: number;
  scored: number;
  /** 평가 불가 + 채점 대기 — a replay exists but no number. */
  unscored: number;
  failed: number;
  skipped: number;
  inFlight: number;
  /** Scored on another rubric or policy — counted, never averaged (R10). */
  excluded: number;
  /** Scored with the common rubric — its own measure, averaged only among themselves (R16). */
  common: number;
  /** Mean of the common-rubric cells. `null` when none. */
  commonAvg: number | null;
  /** Mean of comparable scored cells only. `null` when none — never 0 (C5). */
  avg: number | null;
  /** v2's other two scores over the same cells; a missing part is skipped. */
  avgAnswerQuality: number | null;
  avgExecution: number | null;
};

/** A running mean that skips what is not there, instead of reading it as 0. */
export function meanOf(values: readonly (number | null | undefined)[]): number | null {
  const present = values.filter((v): v is number => typeof v === "number");
  return present.length ? present.reduce((s, v) => s + v, 0) / present.length : null;
}

export function runStats(
  cells: Record<string, BatchCell> | undefined,
  testCaseIds: readonly string[],
): RunStats {
  const stats: RunStats = {
    total: testCaseIds.length,
    scored: 0,
    unscored: 0,
    failed: 0,
    skipped: 0,
    inFlight: 0,
    excluded: 0,
    common: 0,
    commonAvg: null,
    avg: null,
    avgAnswerQuality: null,
    avgExecution: null,
  };
  let sum = 0;
  let commonSum = 0;
  const answerQuality: (number | null | undefined)[] = [];
  const execution: (number | null | undefined)[] = [];
  for (const id of testCaseIds) {
    const cell = cells?.[id];
    if (!cell) continue;
    if (cell.status === "pending" || cell.status === "running") stats.inFlight += 1;
    else if (cell.status === "skipped") stats.skipped += 1;
    else if (cell.status === "failed") stats.failed += 1;
    else {
      const outcome = evaluationOutcomeFrom(cell.evaluation);
      if (outcome.status === "scored" && isCommon(outcome)) {
        stats.common += 1;
        commonSum += outcome.score;
      } else if (outcome.status === "scored" && !isComparable(outcome)) stats.excluded += 1;
      else if (outcome.status === "scored") {
        stats.scored += 1;
        sum += outcome.score;
        answerQuality.push(outcome.answer_quality);
        execution.push(outcome.execution);
      } else if (outcome.status === "failed") stats.failed += 1;
      else stats.unscored += 1;
    }
  }
  stats.avg = stats.scored ? sum / stats.scored : null;
  stats.commonAvg = stats.common ? commonSum / stats.common : null;
  stats.avgAnswerQuality = meanOf(answerQuality);
  stats.avgExecution = meanOf(execution);
  return stats;
}

/** Runs finished so far — the progress bar's numerator. */
export function finishedCount(cells: Record<string, BatchCell> | undefined): number {
  return Object.values(cells ?? {}).filter(
    (c) => c.status !== "pending" && c.status !== "running",
  ).length;
}
